import { useCallback, useEffect } from 'react';

import { useAudioPlayerStreamingContext } from '../context/AudioPlayerStreamingProvider';
import { setActiveContent } from '../state/AudioPlayerState';
import type { PlaylistItemData } from '../ui/playlist/PlaylistItem';

import { useContextMenuActions } from './useContextMenuActions';

type UsePlaylistAutoPlayProps = {
	playlistItems: PlaylistItemData[];
	currentItemId: string | undefined;
};

export const usePlaylistAutoPlay = ({ playlistItems, currentItemId }: UsePlaylistAutoPlayProps) => {
	const { fetchAudio, audioRef } = useAudioPlayerStreamingContext();
	const { removeTrack } = useContextMenuActions();

	const playNextTrack = useCallback(async () => {
		if (playlistItems.length === 0) {
			return;
		}

		const currentIndex = playlistItems.findIndex((item) => item.contentId === currentItemId);

		// Get the current item to remove it
		const currentItem = currentIndex !== -1 ? playlistItems[currentIndex] : null;
		// Remove the finished track if we have the necessary data
		if (currentItem && currentItem.mainContent.ari) {
			try {
				const mainSource = currentItem.mainContent.ari;
				const supportingSources = currentItem.supportingContent?.map(content => content.ari) || [];
				const trackType = currentItem.trackType;

				await removeTrack(mainSource, supportingSources, trackType);
			} catch (_error) {
				// Silently ignore errors - continue to next track even if removal fails
				// leaving this for analytics instrumentation
			}
		}

		let nextTrack: PlaylistItemData;

		if (currentIndex === -1) {
			nextTrack = playlistItems[0];
		} else if (currentIndex === playlistItems.length - 1) {
			// No more tracks to play
			return;
		} else {
			nextTrack = playlistItems[currentIndex + 1];
		}

		setActiveContent({
			contentId: nextTrack.contentId,
			spaceKey: nextTrack.spaceKey || '',
			contentType: nextTrack.contentType || '',
			title: nextTrack.title,
			author: nextTrack.author || '',
			avatarUrl: nextTrack.avatarUrl || '',
			supportingContent: nextTrack.supportingContent || [],
			mainContentLink: nextTrack.mainContent.link,
		});

		fetchAudio();
	}, [currentItemId, playlistItems, fetchAudio, removeTrack]);

	useEffect(() => {
		const audio = audioRef.current;

		if (!audio) {
			return;
		}

		const onEnded = () => {
			playNextTrack();
		};

		audio.addEventListener('ended', onEnded);

		return () => {
			audio.removeEventListener('ended', onEnded);
		};
	}, [audioRef, playNextTrack]);
};
